
import { checkIfUserIsInZoomCollaborateMode } from '@@/bits/zoom'
import device from 'bits/device'
import type { ResizeObserver } from 'bits/global'
import { observeSizeChange } from 'bits/resize_observer'
import { Action, Component, Getter, namespace, State, Vue } from 'bits/vue'
import OzIconButton from 'library/v4/components/OzIconButton.vue'
import { debounce } from 'lodash-es'
import type { User } from 'types'
import AppBarAccountAvatar from 'vuecomponents/AppBarAccountAvatar.vue'
import PadletLogo, { LogoType } from 'vuecomponents/PadletLogo.vue'
import SurfaceTitleMobile from 'vuecomponents/SurfaceTitleMobile.vue'

@Component({
  components: {
    OzIconButton,
    PadletLogo,
    AppBarAccountAvatar,
    SurfaceTitleMobile,
  },
})
export default class SurfaceHeaderMobile extends Vue {
  $el!: HTMLDivElement

  device = device

  @State isEmbedded!: boolean
  $refs!: { surfaceTitle: SurfaceTitleMobile; surfaceNavbar: HTMLDivElement }
  @State isElectronApp!: boolean
  @State xHeader!: boolean
  @State user!: User
  @State isTouchable!: boolean
  @State isUserHostingZoomCollaboration!: boolean
  @Getter headerHeight!: number
  @Getter dir!: 'ltr' | 'rtl'
  @Getter backgroundColor!: string
  @Getter headerColor!: string
  @Getter backgroundLuminance!: 'light' | 'dark'
  @Getter colorScheme!: 'light' | 'dark'
  @Getter isMap!: boolean
  @Getter amIRegistered!: boolean
  @Getter homeUrl!: string
  @Getter isTitleBarTransparent!: boolean
  @Getter canIAdminister!: boolean
  @Getter isSectionBreakout!: boolean
  @Action resizeHeader!: (props: { height: number }) => void
  @Action checkAndSubscribeToPushNotifications!: () => void
  @namespace('settings').Action toggleSettingsPanel!: () => void

  resizeObserver: ResizeObserver | null = null

  get logoType(): LogoType {
    if (this.isMap) {
      return this.colorScheme === 'light' ? LogoType.Dark300 : LogoType.Light300
    }
    return this.backgroundLuminance === 'light' ? LogoType.Dark300 : LogoType.Light300
  }

  get realBackgroundLuminance(): 'light' | 'dark' {
    if (this.isMap) {
      return this.colorScheme
    }
    return this.backgroundLuminance
  }

  async mounted(): Promise<void> {
    // Don't prompt for notifications if the user is not registered and the app is not the electron app
    if (this.amIRegistered && !this.isElectronApp) this.checkAndSubscribeToPushNotifications()
    this.updateHeaderHeight()
    this.resizeObserver = observeSizeChange(this.$el, debounce(this.updateHeaderHeight, 250))
    this.isUserInZoomCollaborationMode = await checkIfUserIsInZoomCollaborateMode()
  }

  /**
   * ZOOM
   */
  isUserInZoomCollaborationMode = false

  get isZoomCollaborationGuest(): boolean {
    return this.isUserInZoomCollaborationMode === true && this.isUserHostingZoomCollaboration === false
  }

  beforeDestroy(): void {
    this.resizeObserver?.disconnect()
  }

  updateHeaderHeight(): void {
    // Since the resize is debounced, we need to check
    // that these elements still exist.
    if (!this.$refs.surfaceNavbar || !this.$refs.surfaceTitle) return
    if (!this.isCollapsed) {
      this.$nextTick(() => {
        this.resizeHeader({
          height: this.$refs.surfaceNavbar.getBoundingClientRect().height + this.$refs.surfaceTitle.$el.clientHeight,
        })
      })
    } else {
      this.resizeHeader({ height: 48 })
    }
  }

  isCollapsed: boolean | null = null

  collapse(): void {
    this.isCollapsed = true
    this.updateHeaderHeight()
  }

  toggleIsCollapsed(): void {
    this.isCollapsed = !this.isCollapsed
    this.updateHeaderHeight()
  }
}
